// Чистые помощники слоя платформ раздела «Буи»: подписи и цвета, выражение
// образа для MapLibre, элементы легенды. Отрисовка силуэтов и подключение слоёв
// живут в BuoysPage.tsx — здесь только то, что проверяется без карты.
//
// ФОРМА НЕСЁТ ТИП, ЦВЕТ НЕСЁТ СВЕЖЕСТЬ. Здесь сходятся обе конвенции кабинета:
// у спутников цвет различает категорию, у судов — возраст позиции. Типов буёв
// мало и они наглядны, поэтому тип отдан силуэту, а цвет остался под возраст —
// то, ради чего на карту и смотрят.

import type maplibregl from 'maplibre-gl'
import { t } from '../i18n'

/** Типы платформ, которые грузит сервер (см. BUOY_PLATFORM_TYPES бэкенда). */
export const BUOY_TYPES = ['MO', 'DB', 'PF', 'TS', 'FB', 'GL', 'SD', 'CT', 'XB', 'AD'] as const

export type BuoyType = (typeof BUOY_TYPES)[number]

/** Вердикт свежести приезжает С СЕРВЕРА: пороги зависят от типа платформы и
 *  описаны один раз в `src/cabinet/buoy_freshness.py`. Вторая их копия здесь
 *  разъехалась бы молча — так уже расходились палитры карты. */
export type BuoyFreshness = 'fresh' | 'stale' | 'unknown'

/** Образ для типа, которого нет в каталоге. Точка обязана появиться на карте:
 *  пропавшая неотличима от «источник её не прислал». */
export const OTHER_TYPE_ICON = 'other'

export const BUOY_ICON_PREFIX = 'buoy-ic-'

/** Ключ подписи типа в каталоге переводов. */
export function typeLabelKey(type: string): string {
  return `buoys.type.${type}`
}

/** Подпись типа; неизвестный код показывается КАК ЕСТЬ.
 *
 *  Выдуманное имя пережило бы верификацию и осталось бы в витрине навсегда —
 *  то же правило, что у кодов видов РФМО. */
export function typeLabel(type: string): string {
  if (!(BUOY_TYPES as readonly string[]).includes(type)) return type
  return t(typeLabelKey(type))
}

/** Цвет по свежести. Три состояния, а не два: «не знаем» — это не «устарел». */
export function freshnessColor(state: BuoyFreshness): string {
  switch (state) {
    case 'fresh':
      return '#22d3ee'
    case 'stale':
      return '#94a3b8'
    default:
      return '#c084fc'
  }
}

/** Идентификатор образа: силуэт типа, окрашенный по свежести. */
export function buoyIconId(type: string | null | undefined, state: BuoyFreshness): string {
  const known = type && (BUOY_TYPES as readonly string[]).includes(type) ? type : OTHER_TYPE_ICON
  return `${BUOY_ICON_PREFIX}${known}-${state}`
}

/** Все образы, которые страница обязана зарегистрировать до добавления слоя. */
export function iconMatrix(): Array<{ id: string; type: string; state: BuoyFreshness }> {
  const states: BuoyFreshness[] = ['fresh', 'stale', 'unknown']
  const types = [...BUOY_TYPES, OTHER_TYPE_ICON]
  return types.flatMap(type =>
    states.map(state => ({ id: `${BUOY_ICON_PREFIX}${type}-${state}`, type, state })),
  )
}

/** data-driven icon-image: `buoy-ic-` + тип + `-` + свежесть.
 *
 *  `match` по известным типам, а не `concat` с сырым свойством: неизвестный код
 *  дал бы имя образа, которого нет, и MapLibre молча не нарисовал бы точку. */
export function buoyIconExpr(): maplibregl.ExpressionSpecification {
  const typePart: unknown[] = ['match', ['get', 'platform_type']]
  for (const type of BUOY_TYPES) typePart.push(type, type)
  typePart.push(OTHER_TYPE_ICON)

  return [
    'concat',
    BUOY_ICON_PREFIX,
    typePart,
    '-',
    ['match', ['get', 'freshness'], 'fresh', 'fresh', 'stale', 'stale', 'unknown'],
  ] as unknown as maplibregl.ExpressionSpecification
}

export interface LegendItem {
  type: string
  label: string
  count: number
}

/** Легенда строится по фасетам КАДРА: тип, которого в кадре нет, в ней не
 *  нужен, а тип, который есть, обязан быть назван. */
export function legendItems(
  facets: Array<{ value: string; count: number }> | null | undefined,
): LegendItem[] {
  return (facets ?? []).map(f => ({
    type: f.value,
    label: typeLabel(f.value),
    count: f.count,
  }))
}
